/**
 * Task list machine - replaces useTaskStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { Task, Board, BoardColumn, DragState, TaskFilters, TaskSort } from '../types'

const defaultFilters: TaskFilters = {}
const defaultSort: TaskSort = { by: 'createdAt', order: 'desc' }
const defaultDragState: DragState = {
  isDragging: false,
  draggedTaskId: null,
  sourceColumnId: null,
  targetColumnId: null,
}

export interface TaskListContext {
  tasks: Task[]
  selectedTaskIds: string[]
  currentBoard: Board | null
  boards: Board[]
  viewMode: 'list' | 'board'
  filters: TaskFilters
  sort: TaskSort
  dragState: DragState
}

export type TaskListEvent =
  | { type: 'SET_TASKS'; tasks: Task[] }
  | { type: 'ADD_TASK'; task: Task }
  | { type: 'UPDATE_TASK'; id: string; updates: Partial<Task> }
  | { type: 'REMOVE_TASK'; id: string }
  | { type: 'SET_SELECTED_TASK_IDS'; ids: string[] }
  | { type: 'TOGGLE_TASK_SELECTION'; id: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_CURRENT_BOARD'; board: Board | null }
  | { type: 'SET_BOARDS'; boards: Board[] }
  | { type: 'UPDATE_BOARD'; id: string; updates: Partial<Board> }
  | { type: 'ADD_COLUMN'; column: BoardColumn }
  | { type: 'UPDATE_COLUMN'; id: string; updates: Partial<BoardColumn> }
  | { type: 'REMOVE_COLUMN'; id: string }
  | { type: 'REORDER_COLUMNS'; startIndex: number; endIndex: number }
  | { type: 'SET_VIEW_MODE'; mode: 'list' | 'board' }
  | { type: 'SET_FILTERS'; filters: Partial<TaskFilters> }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_SORT'; sort: Partial<TaskSort> }
  | { type: 'START_DRAG'; taskId: string; columnId: string }
  | { type: 'END_DRAG' }
  | { type: 'SET_DRAG_TARGET'; columnId: string | null }
  | { type: 'MOVE_TASK'; taskId: string; targetColumnId: string; position?: number }

const machineSetup = setup({
  types: {} as { context: TaskListContext; events: TaskListEvent },
})

export const taskListMachine = machineSetup.createMachine({
  id: 'taskList',
  context: {
    tasks: [],
    selectedTaskIds: [],
    currentBoard: null,
    boards: [],
    viewMode: 'board',
    filters: defaultFilters,
    sort: defaultSort,
    dragState: defaultDragState,
  },
  on: {
    SET_TASKS: {
      actions: assign({
        tasks: ({ event }) => (event.type === 'SET_TASKS' ? event.tasks : []),
      }),
    },
    ADD_TASK: {
      actions: assign({
        tasks: ({ context, event }) =>
          event.type === 'ADD_TASK' ? [event.task, ...context.tasks] : context.tasks,
      }),
    },
    UPDATE_TASK: {
      actions: assign({
        tasks: ({ context, event }) => {
          if (event.type !== 'UPDATE_TASK') return context.tasks
          return context.tasks.map((t) => (t.id === event.id ? { ...t, ...event.updates } : t))
        },
      }),
    },
    REMOVE_TASK: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'REMOVE_TASK') return {}
        return {
          tasks: context.tasks.filter((t) => t.id !== event.id),
          selectedTaskIds: context.selectedTaskIds.filter((id) => id !== event.id),
        }
      }),
    },
    SET_SELECTED_TASK_IDS: {
      actions: assign({
        selectedTaskIds: ({ event }) => (event.type === 'SET_SELECTED_TASK_IDS' ? event.ids : []),
      }),
    },
    TOGGLE_TASK_SELECTION: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'TOGGLE_TASK_SELECTION') return {}
        const id = event.id
        const has = context.selectedTaskIds.includes(id)
        return {
          selectedTaskIds: has
            ? context.selectedTaskIds.filter((x) => x !== id)
            : [...context.selectedTaskIds, id],
        }
      }),
    },
    CLEAR_SELECTION: {
      actions: assign({ selectedTaskIds: [] }),
    },
    SET_CURRENT_BOARD: {
      actions: assign({
        currentBoard: ({ event }) => (event.type === 'SET_CURRENT_BOARD' ? event.board : null),
      }),
    },
    SET_BOARDS: {
      actions: assign({
        boards: ({ event }) => (event.type === 'SET_BOARDS' ? event.boards : []),
      }),
    },
    UPDATE_BOARD: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'UPDATE_BOARD') return {}
        return {
          boards: context.boards.map((b) => (b.id === event.id ? { ...b, ...event.updates } : b)),
          currentBoard:
            context.currentBoard?.id === event.id
              ? { ...context.currentBoard, ...event.updates }
              : context.currentBoard,
        }
      }),
    },
    ADD_COLUMN: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'ADD_COLUMN' || !context.currentBoard) return {}
        return {
          currentBoard: {
            ...context.currentBoard,
            columns: [...context.currentBoard.columns, event.column],
          },
        }
      }),
    },
    UPDATE_COLUMN: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'UPDATE_COLUMN' || !context.currentBoard) return {}
        return {
          currentBoard: {
            ...context.currentBoard,
            columns: context.currentBoard.columns.map((c) =>
              c.id === event.id ? { ...c, ...event.updates } : c
            ),
          },
        }
      }),
    },
    REMOVE_COLUMN: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'REMOVE_COLUMN' || !context.currentBoard) return {}
        return {
          currentBoard: {
            ...context.currentBoard,
            columns: context.currentBoard.columns.filter((c) => c.id !== event.id),
          },
        }
      }),
    },
    REORDER_COLUMNS: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'REORDER_COLUMNS' || !context.currentBoard) return {}
        const { startIndex, endIndex } = event
        const columns = [...context.currentBoard.columns]
        const [removed] = columns.splice(startIndex, 1)
        if (!removed) return {}
        columns.splice(endIndex, 0, removed)
        const reordered = columns.map((c, i) => ({ ...c, position: i }))
        return {
          currentBoard: { ...context.currentBoard, columns: reordered },
        }
      }),
    },
    SET_VIEW_MODE: {
      actions: assign({
        viewMode: ({ event }) => (event.type === 'SET_VIEW_MODE' ? event.mode : 'board'),
      }),
    },
    SET_FILTERS: {
      actions: assign({
        filters: ({ context, event }) =>
          event.type === 'SET_FILTERS' ? { ...context.filters, ...event.filters } : context.filters,
      }),
    },
    CLEAR_FILTERS: {
      actions: assign({ filters: defaultFilters }),
    },
    SET_SORT: {
      actions: assign({
        sort: ({ context, event }) =>
          event.type === 'SET_SORT' ? { ...context.sort, ...event.sort } : context.sort,
      }),
    },
    START_DRAG: {
      actions: assign({
        dragState: ({ event }) =>
          event.type === 'START_DRAG'
            ? {
                isDragging: true,
                draggedTaskId: event.taskId,
                sourceColumnId: event.columnId,
                targetColumnId: null,
              }
            : defaultDragState,
      }),
    },
    END_DRAG: {
      actions: assign({ dragState: defaultDragState }),
    },
    SET_DRAG_TARGET: {
      actions: assign({
        dragState: ({ context, event }) =>
          event.type === 'SET_DRAG_TARGET'
            ? { ...context.dragState, targetColumnId: event.columnId }
            : context.dragState,
      }),
    },
    MOVE_TASK: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'MOVE_TASK') return {}
        const task = context.tasks.find((t) => t.id === event.taskId)
        const targetColumn = context.currentBoard?.columns.find(
          (c) => c.id === event.targetColumnId
        )
        if (!task || !targetColumn) return { dragState: defaultDragState }
        const updatedTask: Task = {
          ...task,
          columnId: event.targetColumnId,
          status: targetColumn.status,
          position: event.position ?? task.position ?? 0,
        }
        return {
          tasks: context.tasks.map((t) => (t.id === event.taskId ? updatedTask : t)),
          dragState: defaultDragState,
        }
      }),
    },
  },
})
